import React, { useState } from "react";

const Brand = () => {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center py-12 px-4">
      <div className="w-full max-w-2xl mx-auto">
        <h1 className="text-4xl font-bold font-primary text-center mb-2">
          FOZZIL Brand Guidelines
        </h1>
        <div className="text-gray-300 italic text-center mb-10 text-lg">
          Effective Date: September 10th, 2025
        </div>

        <h2 className="text-2xl font-bold mb-3 mt-10">About FOZZIL</h2>
        <p className="mb-8 text-lg leading-relaxed">
          FOZZIL, the innovator behind cutting-edge AI technologies and systems,
          owns exclusive rights to its trademarks, logos, intellectual property,
          and branding—including the names “FOZZIL,” “ZYPHER AI,” and related
          elements. These guidelines outline how you may properly reference and
          use our brand assets as a valued user or supporter of FOZZIL.
          <br />
          <span className="block mt-2 text-gray-400">
            Note: FOZZIL is an independent company and not affiliated with any
            other organization unless explicitly stated.
          </span>
        </p>

        <h2 className="text-2xl font-bold mb-3 mt-10">
          Attributing FOZZIL Content
        </h2>
        <p className="mb-4 text-lg">
          If you publish or distribute any text, visuals, or other content
          generated using FOZZIL’s services or platforms, please include a clear
          and legible attribution such as:
        </p>
        <ul className="mb-8 text-lg list-disc list-inside space-y-1">
          <li>Powered by FOZZIL</li>
          <li>Created with FOZZIL AI</li>
          <li>Brought to life using FOZZIL Intelligence</li>
        </ul>

        <h2 className="text-2xl font-bold mb-3 mt-10">
          Media and Press Engagement
        </h2>
        <p className="mb-8 text-lg leading-relaxed">
          If you’d like to include FOZZIL in press releases or public-facing
          media, please reach out to us at{" "}
          <a
            href="mailto:legal@fozzil.in"
            className="underline hover:text-blue-400"
          >
            legal@fozzil.in
          </a>{" "}
          for approval. When referencing FOZZIL, kindly identify us as:
          <br />
          <span className="block mt-2 font-medium text-gray-200">
            “FOZZIL is an AI research and development company dedicated to
            building intelligent systems that accelerate progress in General,
            healthcare, scientific exploration, and beyond.”
          </span>
        </p>

        <h2 className="text-2xl font-bold mb-3 mt-10">Usage Terms</h2>
        <p className="mb-4 text-lg">
          The term “Brand Assets” includes FOZZIL’s names, trademarks, service
          marks, logos, product names, icons, and other design elements that
          represent the FOZZIL brand.
          <br />
          By using any FOZZIL Brand Asset, you agree to the following
          conditions:
        </p>
        <div className="flex flex-col md:flex-row gap-8 mb-8">
          <div className="flex-1">
            <h3 className="font-semibold mb-2 text-lg">You May:</h3>
            <ul className="list-disc list-inside space-y-1 text-lg">
              <li>
                Use FOZZIL’s names or logos solely to accurately refer to our
                company or services.
              </li>
              <li>
                Adhere to these FOZZIL Brand Guidelines and any future updates
                we issue.
              </li>
              <li>
                Make adjustments to your usage of our assets if requested by our
                legal or brand team.
              </li>
            </ul>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold mb-2 text-lg">You May Not:</h3>
            <ul className="list-disc list-inside space-y-1 text-lg">
              <li>
                Use our brand in a misleading way, or suggest FOZZIL endorses,
                approves, or is associated with your product, service, or
                organization without written permission.
              </li>
              <li>
                Use FOZZIL branding in the name of your app, product, service,
                business, or domain.
              </li>
              <li>
                Alter, distort, or modify our logos or combine them with other
                graphics, symbols, or text.
              </li>
              <li>
                Use our branding to create a new mark, logo, or brand element.
              </li>
              <li>
                Use FOZZIL brand elements as part of your own brand identity.
              </li>
            </ul>
          </div>
        </div>
        <p className="mb-8 text-lg">
          We reserve the right to revoke any permission to use our brand
          elements at our discretion. If revoked, you must promptly discontinue
          usage.
        </p>

        <h2 className="text-2xl font-bold mb-3 mt-10">Logo Use</h2>
        <p className="mb-8 text-lg">
          You are permitted to use our official logos only as they are
          provided—unaltered and unedited. Any resizing, recoloring, cropping,
          or stylization of our logos is strictly prohibited.
        </p>

        <h2 className="text-2xl font-bold mb-3 mt-14">Logos</h2>
        <p className="mb-6 text-lg text-gray-200">
          By using our logos, you agree to the Usage Terms above and further
          agree to only use our logos exactly as provided at the download link
          below, without any alteration or adjustment.
        </p>
        <div className="mb-4 flex items-center gap-2">
          <input
            type="checkbox"
            id="logo-terms"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="w-5 h-5 accent-white bg-black border border-gray-400 rounded"
          />
          <label
            htmlFor="logo-terms"
            className="text-lg text-white select-none"
          >
            By using our logos, you agree to our Marks{" "}
            <a href="#usage-terms" className="underline hover:text-blue-400">
              usage terms
            </a>
            .
          </label>
        </div>
        <a
          href={
            agreed
              ? "https://drive.google.com/uc?export=download&id=1DumUNuGP8QwgqRAEKU8jrA2Z5a3aB5-V"
              : undefined
          }
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-block px-6 py-2 border border-white rounded-full text-lg font-mono tracking-widest text-white transition-colors duration-200 mb-10 ${
            agreed
              ? "hover:bg-white hover:text-black cursor-pointer"
              : "opacity-50 cursor-not-allowed pointer-events-none"
          }`}
          download={agreed ? true : undefined}
        >
          DOWNLOAD OUR LOGOS
        </a>

        <h2 className="text-2xl font-bold mb-3 mt-10">Contact FOZZIL</h2>
        <p className="mb-2 text-lg">
          For brand, legal, or usage questions, reach out to{" "}
          <a
            href="mailto:legal@fozzil.in"
            className="underline hover:text-blue-400"
          >
            legal@fozzil.in
          </a>
        </p>
      </div>
    </div>
  );
};

export default Brand;
